import { selectBundlePagesData, useAppSelector, selectPageInfoCache } from '@/store';
import { useCallback, useEffect, useState } from 'react';

export const useAnnotationLabels = () => {
  const [showFilteredLabels, setShowFilteredLabels] = useState<boolean>(false);
  const [filterAnnotationLabels, setFilterAnnotationLabels] = useState<FilterAnnotationLabels>({});

  const bundlePagesData = useAppSelector(selectBundlePagesData);
  const pageInfoCache = useAppSelector(selectPageInfoCache);

  useEffect(() => {
    if (bundlePagesData.size) {
      const updatedFilterAnnotationLabels: FilterAnnotationLabels = {};

      bundlePagesData.forEach((item) => {
        const cachedInfo = pageInfoCache[item.pageId];
        const effectiveTextLabels = cachedInfo?.textLabels ?? item.textLabels;
        const effectiveDates = cachedInfo?.dates ?? item.dates;

        effectiveTextLabels?.forEach((textLabel) => {
          const pageId = item.pageId;
          const label = textLabel.labelValue;

          if (!updatedFilterAnnotationLabels[pageId]) {
            updatedFilterAnnotationLabels[pageId] = {};
          }

          if (!(label in updatedFilterAnnotationLabels[pageId])) {
            updatedFilterAnnotationLabels[pageId][label] = true;
          }
        });

        effectiveDates?.forEach((dateItem) => {
          const pageId = item.pageId;
          const label = `${dateItem.date} / ${dateItem.dateCategory}`;

          if (!updatedFilterAnnotationLabels[pageId]) {
            updatedFilterAnnotationLabels[pageId] = {};
          }

          if (!(label in updatedFilterAnnotationLabels[pageId])) {
            updatedFilterAnnotationLabels[pageId][label] = true;
          }
        });

        return updatedFilterAnnotationLabels;
      });

      setFilterAnnotationLabels(updatedFilterAnnotationLabels);
    }
  }, [bundlePagesData, pageInfoCache]);

  const addOrUpdateAnnotationLabel = useCallback((pageId: string, label: string, value: boolean) => {
    setFilterAnnotationLabels((prev) => {
      const updated = { ...prev };
      if (!updated[pageId]) {
        updated[pageId] = {};
      }
      updated[pageId][label] = value;
      return updated;
    });
  }, []);

  const resetAnnotationLabels = useCallback((pageId?: string, value: boolean = true) => {
    setFilterAnnotationLabels((prev) => {
      const updated = { ...prev };
      if (pageId) {
        if (updated[pageId]) {
          Object.keys(updated[pageId]).forEach((label) => {
            updated[pageId][label] = value;
          });
        }
      } else {
        Object.keys(updated).forEach((id) => {
          Object.keys(updated[id]).forEach((label) => {
            updated[id][label] = value;
          });
        });
      }
      return updated;
    });
  }, []);

  return {
    showFilteredLabels,
    setShowFilteredLabels,
    filterAnnotationLabels,
    addOrUpdateAnnotationLabel,
    resetAnnotationLabels
  };
};
